import React from 'react';
import { generatePath, Outlet } from 'react-router';

import { PERMISSIONS } from 'api/auth/constants';
import { ICONS_MAP } from 'constants/icons';
import { PERMISSION_TYPE } from 'hooks/useRBAC';
import { ROUTES } from 'routes/config/constants';
import { IRoutesConfigItem } from 'routes/config/index';
import { appLazy } from 'routes/ErrorBoundary/appReloadLazy';

const RoutingRuleset = appLazy(
  () => import('routes/pages/AuthorizedStack/TrafficManagement/RoutingRuleset'),
);
const CreateRoutingRuleset = appLazy(
  () =>
    import(
      'routes/pages/AuthorizedStack/TrafficManagement/RoutingRuleset/Ruleset/Create'
    ),
);
const CopyRoutingRuleset = appLazy(
  () =>
    import('routes/pages/AuthorizedStack/TrafficManagement/RoutingRuleset/Ruleset/Copy'),
);
const EditRoutingRuleset = appLazy(
  () =>
    import('routes/pages/AuthorizedStack/TrafficManagement/RoutingRuleset/Ruleset/Edit'),
);
const BulkCopyRulesets = appLazy(
  () => import('routes/pages/AuthorizedStack/TrafficManagement/RoutingRuleset/BulkCopy'),
);
const CopyToMerchant = appLazy(
  () =>
    import(
      'routes/pages/AuthorizedStack/TrafficManagement/RoutingRuleset/CopyToMerchant'
    ),
);

const CountryGroups = appLazy(
  () => import('routes/pages/AuthorizedStack/TrafficManagement/CountryGroups'),
);
const CopyCountryGroup = appLazy(
  () =>
    import(
      'routes/pages/AuthorizedStack/TrafficManagement/CountryGroups/CountryGroup/Copy'
    ),
);
const CreateCountryGroup = appLazy(
  () =>
    import(
      'routes/pages/AuthorizedStack/TrafficManagement/CountryGroups/CountryGroup/Create'
    ),
);
const EditCountryGroup = appLazy(
  () =>
    import(
      'routes/pages/AuthorizedStack/TrafficManagement/CountryGroups/CountryGroup/Edit'
    ),
);

const BinGroups = appLazy(
  () => import('routes/pages/AuthorizedStack/TrafficManagement/BinGroups'),
);
const CreateBinGroup = appLazy(
  () =>
    import('routes/pages/AuthorizedStack/TrafficManagement/BinGroups/BinGroup/Create'),
);
const EditBinGroup = appLazy(
  () => import('routes/pages/AuthorizedStack/TrafficManagement/BinGroups/BinGroup/Edit'),
);

const MetadataTemplates = appLazy(
  () => import('routes/pages/AuthorizedStack/TrafficManagement/MetadataTemplates'),
);
const CreateMetadataTemplate = appLazy(
  () =>
    import(
      'routes/pages/AuthorizedStack/TrafficManagement/MetadataTemplates/Template/Create'
    ),
);
const EditMetadataTemplate = appLazy(
  () =>
    import(
      'routes/pages/AuthorizedStack/TrafficManagement/MetadataTemplates/Template/Edit'
    ),
);

const MerchantGroups = appLazy(
  () => import('routes/pages/AuthorizedStack/TrafficManagement/MerchantGroups'),
);
const CreateMerchantGroup = appLazy(
  () => import('routes/pages/AuthorizedStack/TrafficManagement/MerchantGroups/Create'),
);
const EditMerchantGroup = appLazy(
  () => import('routes/pages/AuthorizedStack/TrafficManagement/MerchantGroups/Edit'),
);
const Blacklists = appLazy(
  () => import('routes/pages/AuthorizedStack/TrafficManagement/Blacklists'),
);
const CreateBlackListCardMask = appLazy(
  () =>
    import(
      'routes/pages/AuthorizedStack/TrafficManagement/Blacklists/CardMasks/CardMask/Create'
    ),
);
const CreateBlackListIpAddress = appLazy(
  () =>
    import(
      'routes/pages/AuthorizedStack/TrafficManagement/Blacklists/IpAddresses/IpAddress/Create'
    ),
);
const CreateBlackListEmail = appLazy(
  () =>
    import(
      'routes/pages/AuthorizedStack/TrafficManagement/Blacklists/Emails/Email/Create'
    ),
);
const CreateBlackListDevice = appLazy(
  () =>
    import(
      'routes/pages/AuthorizedStack/TrafficManagement/Blacklists/Devices/Device/Create'
    ),
);
const CreateBlackListCardPan = appLazy(
  () =>
    import(
      'routes/pages/AuthorizedStack/TrafficManagement/Blacklists/CardPans/CardPan/Create'
    ),
);
const ChangePaymentMethodRules = appLazy(
  () => import('routes/pages/AuthorizedStack/TrafficManagement/ChangePaymentMethodRules'),
);
const BulkCopyChangeRules = appLazy(
  () =>
    import(
      'routes/pages/AuthorizedStack/TrafficManagement/ChangePaymentMethodRules/BulkCopy'
    ),
);
const BulkCreateChangeRule = appLazy(
  () =>
    import(
      'routes/pages/AuthorizedStack/TrafficManagement/ChangePaymentMethodRules/BulkCreate'
    ),
);

const AuditLogs = appLazy(() => import('routes/pages/AuthorizedStack/AuditLogs'));

export const TRAFFIC_MANAGEMENT_CHILDREN: IRoutesConfigItem[] = [
  {
    breadcrumb: 'routingRuleset.title',
    children: [
      {
        component: RoutingRuleset,
        path: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.ROUTING_RULESET.PATH,
        permissions: [PERMISSIONS.CAN_VIEW_RULESETS],
      },
      {
        component: CreateRoutingRuleset,
        path: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.ROUTING_RULESET.SUB_PATH.CREATE,
        permissions: [PERMISSIONS.CAN_CREATE_RULESET],
      },
      {
        breadcrumb: (params) => {
          return params?.id || 'routingRuleset.title';
        },
        component: EditRoutingRuleset,
        path: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.ROUTING_RULESET.SUB_PATH.EDIT.PATH,
        permissions: [PERMISSIONS.CAN_UPDATE_RULESET],
        url: (params) => {
          const id = params?.id;
          const { PARAMS, PATH } =
            ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.ROUTING_RULESET.SUB_PATH.EDIT;
          return !id ? '' : generatePath(PATH, { [PARAMS.ID]: id });
        },
      },
      {
        breadcrumb: (params) => {
          return params?.id || 'routingRuleset.title';
        },
        component: CopyRoutingRuleset,
        path: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.ROUTING_RULESET.SUB_PATH.COPY.PATH,
        permissions: [PERMISSIONS.CAN_CREATE_RULESET],
        url: (params) => {
          const id = params?.id;
          const { PARAMS, PATH } =
            ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.ROUTING_RULESET.SUB_PATH.COPY;
          return !id ? '' : generatePath(PATH, { [PARAMS.ID]: id });
        },
      },
      {
        component: CopyToMerchant,
        path: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.ROUTING_RULESET.SUB_PATH
          .COPY_TO_MERCHANT,
        permissions: [PERMISSIONS.CAN_COPY_RULESETS_TO_MERCHANT],
      },
      {
        component: BulkCopyRulesets,
        path: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.ROUTING_RULESET.SUB_PATH.BULK_COPY,
        permissions: [PERMISSIONS.CAN_BULK_COPY_RULESETS],
      },
    ],
    component: Outlet,
    isSubFolder: true,
    navBarIcon: <ICONS_MAP.RoutingRuleset />,
    path: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.ROUTING_RULESET.PATH,
    permissions: [PERMISSIONS.CAN_VIEW_RULESETS],
    testId: 'navbarRoutingRuleset',
  },
  {
    breadcrumb: 'changeRule.rulesPage.title',
    children: [
      {
        component: ChangePaymentMethodRules,
        path: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.CHANGE_PAYMENT_METHOD_RULES.PATH,
        permissions: [PERMISSIONS.CAN_VIEW_CHANGE_RULES],
      },
      {
        breadcrumb: 'changeRule.bulkCopy.title',
        component: BulkCopyChangeRules,
        path: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.CHANGE_PAYMENT_METHOD_RULES.SUB_PATH
          .BULK_COPY,
        permissions: [PERMISSIONS.CAN_BULK_COPY_CHANGE_RULES],
        permissionType: PERMISSION_TYPE.OR,
        url: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.CHANGE_PAYMENT_METHOD_RULES.SUB_PATH
          .BULK_COPY,
      },
      {
        breadcrumb: 'changeRule.bulkCreate.title',
        component: BulkCreateChangeRule,
        path: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.CHANGE_PAYMENT_METHOD_RULES.SUB_PATH
          .BULK_CREATE,
        permissions: [PERMISSIONS.CAN_BULK_CREATE_CHANGE_RULE],
        permissionType: PERMISSION_TYPE.OR,
        url: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.CHANGE_PAYMENT_METHOD_RULES.SUB_PATH
          .BULK_CREATE,
      },
    ],
    component: Outlet,
    isSubFolder: true,
    navBarIcon: <ICONS_MAP.PaymentMethods />,
    path: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.CHANGE_PAYMENT_METHOD_RULES.PATH,
    permissions: [PERMISSIONS.CAN_VIEW_CHANGE_RULES],
    testId: 'navbarChangePaymentMethodRules',
  },
  {
    breadcrumb: 'countryGroups.title',
    children: [
      {
        component: CountryGroups,
        path: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.COUNTRY_GROUPS.PATH,
        permissions: [PERMISSIONS.CAN_VIEW_COUNTRY_GROUPS],
      },
      {
        component: CreateCountryGroup,
        path: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.COUNTRY_GROUPS.SUB_PATH.CREATE,
        permissions: [PERMISSIONS.CAN_CREATE_COUNTRY_GROUPS],
        url: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.COUNTRY_GROUPS.SUB_PATH.CREATE,
      },
      {
        component: EditCountryGroup,
        path: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.COUNTRY_GROUPS.SUB_PATH.EDIT.PATH,
        permissions: [PERMISSIONS.CAN_EDIT_COUNTRY_GROUPS],
        url: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.COUNTRY_GROUPS.SUB_PATH.EDIT.PATH,
      },
      {
        component: CopyCountryGroup,
        path: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.COUNTRY_GROUPS.SUB_PATH.COPY.PATH,
        permissions: [PERMISSIONS.CAN_CREATE_COUNTRY_GROUPS],
        url: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.COUNTRY_GROUPS.SUB_PATH.COPY.PATH,
      },
      {
        breadcrumb: 'auditLogs.title',
        component: AuditLogs,
        path: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.COUNTRY_GROUPS.SUB_PATH.AUDIT_LOGS.PATH,
        permissions: [PERMISSIONS.CAN_CREATE_COUNTRY_GROUPS, PERMISSIONS.VIEW_AUDIT_LOGS],
        url: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.COUNTRY_GROUPS.SUB_PATH.AUDIT_LOGS.PATH,
      },
    ],
    component: Outlet,
    isSubFolder: true,
    navBarIcon: <ICONS_MAP.CountryGroups />,
    path: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.COUNTRY_GROUPS.PATH,
    permissions: [PERMISSIONS.CAN_VIEW_COUNTRY_GROUPS],
    testId: 'navbarCountryGroups',
    url: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.COUNTRY_GROUPS.PATH,
  },
  {
    breadcrumb: 'binGroups.title',
    children: [
      {
        component: BinGroups,
        path: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.BIN_GROUPS.PATH,
        permissions: [PERMISSIONS.CAN_VIEW_BIN_GROUPS],
      },
      {
        component: CreateBinGroup,
        path: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.BIN_GROUPS.SUB_PATH.CREATE,
        permissions: [PERMISSIONS.CAN_CREATE_BIN_GROUP],
        url: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.BIN_GROUPS.SUB_PATH.CREATE,
      },
      {
        component: EditBinGroup,
        path: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.BIN_GROUPS.SUB_PATH.EDIT.PATH,
        permissions: [PERMISSIONS.CAN_EDIT_BIN_GROUP],
        url: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.BIN_GROUPS.SUB_PATH.EDIT.PATH,
      },
      {
        breadcrumb: 'auditLogs.title',
        component: AuditLogs,
        path: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.BIN_GROUPS.SUB_PATH.AUDIT_LOGS.PATH,
        permissions: [PERMISSIONS.CAN_CREATE_BIN_GROUP, PERMISSIONS.VIEW_AUDIT_LOGS],
        url: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.BIN_GROUPS.SUB_PATH.AUDIT_LOGS.PATH,
      },
    ],
    component: Outlet,
    isSubFolder: true,
    navBarIcon: <ICONS_MAP.BinGroups />,
    path: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.BIN_GROUPS.PATH,
    permissions: [PERMISSIONS.CAN_VIEW_BIN_GROUPS],
    testId: 'navbarBinGroups',
    url: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.BIN_GROUPS.PATH,
  },
  {
    breadcrumb: 'metadataTemplates.title',
    children: [
      {
        component: MetadataTemplates,
        path: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.METADATA_TEMPLATES.PATH,
        permissions: [PERMISSIONS.CAN_VIEW_METADATA_TEMPLATES],
      },
      {
        component: CreateMetadataTemplate,
        path: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.METADATA_TEMPLATES.SUB_PATH.CREATE,
        permissions: [PERMISSIONS.CAN_CREATE_METADATA_TEMPLATE],
        url: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.METADATA_TEMPLATES.SUB_PATH.CREATE,
      },
      {
        component: EditMetadataTemplate,
        path: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.METADATA_TEMPLATES.SUB_PATH.EDIT.PATH,
        permissions: [PERMISSIONS.CAN_EDIT_METADATA_TEMPLATE],
        url: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.METADATA_TEMPLATES.SUB_PATH.EDIT.PATH,
      },
    ],
    component: Outlet,
    isSubFolder: true,
    navBarIcon: <ICONS_MAP.MetadataTemplates />,
    path: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.METADATA_TEMPLATES.PATH,
    permissions: [PERMISSIONS.CAN_VIEW_METADATA_TEMPLATES],
    testId: 'navbarMetadataTemplates',
    url: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.METADATA_TEMPLATES.PATH,
  },
  {
    breadcrumb: 'merchantGroups.title',
    children: [
      {
        component: MerchantGroups,
        path: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.MERCHANT_GROUPS.PATH,
        permissions: [PERMISSIONS.CAN_VIEW_MERCHANT_GROUPS],
      },
      {
        breadcrumb: 'merchantGroups.create',
        component: CreateMerchantGroup,
        path: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.MERCHANT_GROUPS.SUB_PATH.CREATE,
        permissions: [PERMISSIONS.CAN_CREATE_MERCHANT_GROUPS],
        url: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.MERCHANT_GROUPS.SUB_PATH.CREATE,
      },
      {
        breadcrumb: 'auditLogs.title',
        component: AuditLogs,
        path: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.MERCHANT_GROUPS.SUB_PATH.AUDIT_LOGS.PATH,
        permissions: [
          PERMISSIONS.CAN_CREATE_MERCHANT_GROUPS,
          PERMISSIONS.VIEW_AUDIT_LOGS,
        ],
        url: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.MERCHANT_GROUPS.SUB_PATH.AUDIT_LOGS.PATH,
      },
      {
        breadcrumb: 'merchantGroups.edit',
        component: EditMerchantGroup,
        path: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.MERCHANT_GROUPS.SUB_PATH.EDIT.PATH,
        permissions: [PERMISSIONS.CAN_UPDATE_MERCHANT_GROUPS],
        url: (params) => {
          const id = params?.id;
          const { PARAMS, PATH } =
            ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.MERCHANT_GROUPS.SUB_PATH.EDIT;
          return !id ? '' : generatePath(PATH, { [PARAMS.ID]: id });
        },
      },
    ],
    component: Outlet,
    isSubFolder: true,
    navBarIcon: <ICONS_MAP.MerchantGroups />,
    path: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.MERCHANT_GROUPS.PATH,
    permissions: [PERMISSIONS.CAN_VIEW_MERCHANT_GROUPS],
    testId: 'navbarMerchantGroups',
    url: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.MERCHANT_GROUPS.PATH,
  },
  {
    breadcrumb: 'blacklists.title',
    children: [
      {
        component: Blacklists,
        path: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.BLACKLISTS.PATH,
        permissions: [
          PERMISSIONS.CAN_VIEW_BLACKLISTS_CARD_MASKS,
          PERMISSIONS.CAN_VIEW_BLACKLISTS_EMAILS,
          PERMISSIONS.CAN_VIEW_BLACKLISTS_IP,
          PERMISSIONS.CAN_VIEW_BLACKLISTS_DEVICES,
        ],
        permissionType: PERMISSION_TYPE.OR,
      },
      {
        component: CreateBlackListCardMask,
        path: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.BLACKLISTS.SUB_PATH.CREATE_MASK,
        permissions: [PERMISSIONS.CAN_CREATE_BLACKLISTS_CM],
      },
      {
        component: CreateBlackListEmail,
        path: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.BLACKLISTS.SUB_PATH.CREATE_EMAIL,
        permissions: [PERMISSIONS.CAN_CREATE_BLACKLISTS_EMAIL],
      },
      {
        component: CreateBlackListIpAddress,
        path: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.BLACKLISTS.SUB_PATH.CREATE_IP_ADDRESS,
        permissions: [PERMISSIONS.CAN_CREATE_BLACKLISTS_IP],
      },
      {
        component: CreateBlackListDevice,
        path: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.BLACKLISTS.SUB_PATH.CREATE_DEVICE,
        permissions: [PERMISSIONS.CAN_CREATE_BLACKLISTS_DEVICE],
      },
      {
        component: CreateBlackListDevice,
        path: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.BLACKLISTS.SUB_PATH
          .CREATE_TRANSACTION_DEVICE.PATH,
        permissions: [PERMISSIONS.CAN_CREATE_BLACKLISTS_DEVICE],
      },
      {
        component: CreateBlackListCardPan,
        path: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.BLACKLISTS.SUB_PATH.CREATE_PAN,
        permissions: [PERMISSIONS.CAN_CREATE_BLACKLISTS_CARDS_PANS],
      },
      {
        component: CreateBlackListEmail,
        path: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.BLACKLISTS.SUB_PATH
          .CREATE_TRANSACTION_EMAIL.PATH,
        permissions: [PERMISSIONS.CAN_CREATE_BLACKLISTS_EMAIL],
      },
      {
        component: CreateBlackListCardMask,
        path: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.BLACKLISTS.SUB_PATH
          .CREATE_TRANSACTION_MASK.PATH,
        permissions: [PERMISSIONS.CAN_CREATE_BLACKLISTS_CM],
      },
      {
        component: CreateBlackListIpAddress,
        path: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.BLACKLISTS.SUB_PATH
          .CREATE_TRANSACTION_IP_ADDRESS.PATH,
        permissions: [PERMISSIONS.CAN_CREATE_BLACKLISTS_IP],
      },
      {
        component: CreateBlackListCardPan,
        path: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.BLACKLISTS.SUB_PATH
          .CREATE_TRANSACTION_PAN.PATH,
        permissions: [PERMISSIONS.CAN_CREATE_BLACKLISTS_CARDS_PANS],
      },
      {
        breadcrumb: 'auditLogs.title',
        component: AuditLogs,
        path: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.BLACKLISTS.SUB_PATH.AUDIT_LOGS.PATH,
        permissions: [PERMISSIONS.CAN_CREATE_BLACKLISTS_CM, PERMISSIONS.VIEW_AUDIT_LOGS],
        url: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.BLACKLISTS.SUB_PATH.AUDIT_LOGS.PATH,
      },
    ],
    component: Outlet,
    isSubFolder: true,
    navBarIcon: <ICONS_MAP.Blacklists />,
    path: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.BLACKLISTS.PATH,
    permissions: [
      PERMISSIONS.CAN_VIEW_BLACKLISTS_CARD_MASKS,
      PERMISSIONS.CAN_VIEW_BLACKLISTS_EMAILS,
      PERMISSIONS.CAN_VIEW_BLACKLISTS_IP,
      PERMISSIONS.CAN_VIEW_BLACKLISTS_DEVICES,
    ],
    permissionType: PERMISSION_TYPE.OR,
    testId: 'navbarBlacklists',
    url: ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.BLACKLISTS.PATH,
  },
];
